/**
 * TaskAutomationsModal
 * Manages automation rules scoped to an individual task (scope_task_id).
 * Follows the same pattern as SectionAutomationsModal.
 *
 * Props:
 *   taskId          — UUID of the task; pass null/undefined to keep closed
 *   onClose         — called when the dialog should close
 *   onRulesLoaded   — optional; called after rules are fetched with the count
 *   accountId       — optional; if provided, contacts for that account load into the send_email picker
 *   pipelineId      — optional; filters update_status stage list to this pipeline
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Trash2, Zap, ChevronDown, Check } from "lucide-react";
import {
  api,
  getTaskRules,
  createTaskRule,
  deleteTaskRule,
  getEmailTemplates,
  type TaskRule,
  type EmailTemplate,
  type ProjectTemplate,
  type Contact,
  type WorkflowPipeline,
} from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

interface Props {
  taskId: string | null | undefined;
  onClose: () => void;
  onRulesLoaded?: (count: number) => void;
  accountId?: string | null;
  pipelineId?: string | null;
}

export function TaskAutomationsModal({ taskId, onClose, onRulesLoaded, accountId, pipelineId }: Props) {
  const { toast } = useToast();
  const open = !!taskId;

  // ── Rules state ────────────────────────────────────────────────────────────
  const [rules, setRules] = useState<TaskRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleAdding, setRuleAdding] = useState(false);

  // ── Form state ─────────────────────────────────────────────────────────────
  const [actionType, setActionType] = useState<"" | "change_tag" | "send_email" | "create_project" | "update_status">("");
  const [tagAction, setTagAction] = useState<"add_tag" | "remove_tag">("add_tag");
  const [tagId, setTagId] = useState("");
  const [emailTemplateId, setEmailTemplateId] = useState("");
  const [contactEmails, setContactEmails] = useState<string[]>([]);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [projectTemplateId, setProjectTemplateId] = useState("");
  const [templateSearch, setTemplateSearch] = useState("");
  const [workflowStatusId, setWorkflowStatusId] = useState("");

  // ── Reference data ─────────────────────────────────────────────────────────
  const [availableTags, setAvailableTags] = useState<{ id: string; name: string; color: string }[]>([]);
  const [availableEmailTemplates, setAvailableEmailTemplates] = useState<EmailTemplate[]>([]);
  const [availableProjectTemplates, setAvailableProjectTemplates] = useState<ProjectTemplate[]>([]);
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [availablePipelines, setAvailablePipelines] = useState<WorkflowPipeline[]>([]);

  // ── Reset form helper ──────────────────────────────────────────────────────
  const resetForm = () => {
    setActionType("");
    setTagId("");
    setEmailTemplateId("");
    setContactEmails([]);
    setContactsOpen(false);
    setProjectTemplateId("");
    setTemplateSearch("");
    setWorkflowStatusId("");
  };

  // ── Load on open ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!taskId) return;
    resetForm();
    setRulesLoading(true);

    const safe = <T,>(p: Promise<T>) => p.catch(() => null);
    const loads: Promise<unknown>[] = [
      safe(getTaskRules(taskId)),
      availableTags.length === 0             ? safe(api.getTags())              : Promise.resolve(null),
      availableEmailTemplates.length === 0   ? safe(getEmailTemplates())        : Promise.resolve(null),
      availableProjectTemplates.length === 0 ? safe(api.getProjectTemplates())  : Promise.resolve(null),
      availablePipelines.length === 0        ? safe(api.getWorkflowPipelines()) : Promise.resolve(null),
    ];

    if (accountId && availableContacts.length === 0) {
      loads.push(safe(api.getContacts({ accountId })));
    } else {
      loads.push(Promise.resolve(null));
    }

    Promise.all(loads)
      .then(([fetchedRules, tags, emailTpls, projTpls, pipelines, contacts]) => {
        if (fetchedRules) {
          const loaded = fetchedRules as TaskRule[];
          setRules(loaded);
          onRulesLoaded?.(loaded.length);
        }
        if (tags)      setAvailableTags(tags as typeof availableTags);
        if (emailTpls) setAvailableEmailTemplates(emailTpls as EmailTemplate[]);
        if (projTpls)  setAvailableProjectTemplates(projTpls as ProjectTemplate[]);
        if (pipelines) setAvailablePipelines(pipelines as WorkflowPipeline[]);
        if (contacts)  setAvailableContacts(contacts as Contact[]);
      })
      .catch(() => {/* ignore */})
      .finally(() => setRulesLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  // ── Save rule ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!taskId) return;

    let payload: { action_type: string; action_ref_id?: string; action_config?: Record<string, unknown> } | null = null;

    if (actionType === "change_tag") {
      if (!tagId) return;
      payload = { action_type: tagAction, action_ref_id: tagId };
    } else if (actionType === "send_email") {
      if (!emailTemplateId || contactEmails.length === 0) return;
      payload = { action_type: "send_email", action_ref_id: emailTemplateId, action_config: { contacts: contactEmails } };
    } else if (actionType === "create_project") {
      if (!projectTemplateId) return;
      payload = { action_type: "create_project", action_ref_id: projectTemplateId };
    } else if (actionType === "update_status") {
      if (!workflowStatusId) return;
      payload = { action_type: "update_status", action_ref_id: workflowStatusId };
    } else {
      return;
    }

    setRuleAdding(true);
    try {
      await createTaskRule(taskId, payload);
      const updated = await getTaskRules(taskId);
      setRules(updated);
      onRulesLoaded?.(updated.length);
      resetForm();
      toast({ title: "Rule added" });
    } catch {
      toast({ title: "Failed to add rule", variant: "destructive" });
    } finally {
      setRuleAdding(false);
    }
  };

  // ── Delete rule ────────────────────────────────────────────────────────────
  const handleDelete = async (ruleId: string) => {
    if (!taskId) return;
    try {
      await deleteTaskRule(taskId, ruleId);
      const updated = rules.filter(r => r.id !== ruleId);
      setRules(updated);
      onRulesLoaded?.(updated.length);
      toast({ title: "Rule removed" });
    } catch {
      toast({ title: "Failed to remove rule", variant: "destructive" });
    }
  };

  // ── Save disabled? ─────────────────────────────────────────────────────────
  const saveDisabled =
    ruleAdding ||
    !actionType ||
    (actionType === "change_tag"     && !tagId) ||
    (actionType === "send_email"     && (!emailTemplateId || contactEmails.length === 0)) ||
    (actionType === "create_project" && !projectTemplateId) ||
    (actionType === "update_status"  && !workflowStatusId);

  return (
    <Dialog open={open} onOpenChange={isOpen => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-violet-600" />
            Task Automations
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-1 max-h-[65vh] overflow-y-auto pr-1">
          {rulesLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* ── Existing rules ── */}
              {rules.map(rule => {
                const contacts: string[] = Array.isArray((rule.action_config as Record<string, unknown>)?.contacts)
                  ? (rule.action_config as Record<string, string[]>).contacts
                  : [];
                return (
                  <div key={rule.id} className="rounded-md border border-border bg-muted/30 px-3 py-2.5 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1 leading-snug">
                        {(rule.action_type === "add_tag" || rule.action_type === "remove_tag") && (
                          <span>
                            <span className="text-muted-foreground">When this task is completed, </span>
                            <span className="font-medium">{rule.action_type === "add_tag" ? "add" : "remove"} </span>
                            {rule.action_name ? (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium text-white"
                                style={{ backgroundColor: rule.action_color ?? "#6b7280" }}
                              >
                                {rule.action_name}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs italic">unknown tag</span>
                            )}
                          </span>
                        )}
                        {rule.action_type === "send_email" && (
                          <span>
                            <span className="text-muted-foreground">When this task is completed, send </span>
                            <span className="font-semibold">"{rule.action_name ?? "?"}"</span>
                            <span className="text-muted-foreground"> to:</span>
                            <ul className="mt-1 ml-3 list-disc text-xs text-muted-foreground space-y-0.5">
                              {contacts.length > 0
                                ? contacts.map(c => <li key={c}>{c}</li>)
                                : <li className="italic">No contacts configured</li>}
                            </ul>
                          </span>
                        )}
                        {rule.action_type === "create_project" && (
                          <span>
                            <span className="text-muted-foreground">When this task is completed, create project from template </span>
                            <span className="font-semibold text-violet-700">"{rule.action_name ?? "?"}"</span>
                            <span className="text-muted-foreground">.</span>
                          </span>
                        )}
                        {rule.action_type === "update_status" && (
                          <span>
                            <span className="text-muted-foreground">When this task is completed, set project status to </span>
                            <span className="font-semibold text-blue-700">{rule.action_name ?? "?"}</span>
                            <span className="text-muted-foreground">.</span>
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="p-1 text-muted-foreground hover:text-destructive transition-colors shrink-0 mt-0.5"
                        title="Delete rule"
                        onClick={() => handleDelete(rule.id)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}

              {rules.length === 0 && !rulesLoading && (
                <p className="text-xs text-muted-foreground italic py-1">
                  No automation rules yet. Add one below.
                </p>
              )}

              {/* ── Add rule form ── */}
              <div className="rounded-md border border-border p-3 space-y-3">
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground shrink-0 pt-1.5">When this task is completed</span>
                  <select
                    value={actionType}
                    onChange={e => {
                      setActionType(e.target.value as typeof actionType);
                      setTagId(""); setEmailTemplateId(""); setContactEmails([]);
                      setContactsOpen(false); setProjectTemplateId(""); setTemplateSearch("");
                      setWorkflowStatusId("");
                    }}
                    className="text-xs border border-border rounded px-2 py-1.5 bg-background min-w-[180px]"
                  >
                    <option value="">Select an action</option>
                    <option value="change_tag">Change tag on project</option>
                    <option value="update_status">Update project status</option>
                    <option value="send_email">Send email to client</option>
                    <option value="create_project">Create project</option>
                  </select>

                  {/* ── Change tag sub-fields ── */}
                  {actionType === "change_tag" && (
                    <>
                      <select
                        value={tagAction}
                        onChange={e => setTagAction(e.target.value as "add_tag" | "remove_tag")}
                        className="text-xs border border-border rounded px-2 py-1.5 bg-background"
                      >
                        <option value="add_tag">Add</option>
                        <option value="remove_tag">Remove</option>
                      </select>
                      <select
                        value={tagId}
                        onChange={e => setTagId(e.target.value)}
                        className="text-xs border border-border rounded px-2 py-1.5 bg-background min-w-[160px]"
                      >
                        <option value="">— select tag —</option>
                        {availableTags.map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </>
                  )}

                  {/* ── Send email sub-fields ── */}
                  {actionType === "send_email" && (
                    <>
                      <select
                        value={emailTemplateId}
                        onChange={e => setEmailTemplateId(e.target.value)}
                        className="text-xs border border-border rounded px-2 py-1.5 bg-background min-w-[180px]"
                      >
                        <option value="">Select Template</option>
                        {availableEmailTemplates.map(t => (
                          <option key={t.id} value={t.id}>{t.title}</option>
                        ))}
                      </select>
                      <span className="text-xs text-muted-foreground shrink-0 pt-1.5">to</span>
                      {accountId ? (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setContactsOpen(o => !o)}
                            className="text-xs border border-border rounded px-2 py-1.5 bg-background min-w-[130px] flex items-center gap-1.5"
                          >
                            <span className="flex-1 text-left">
                              {contactEmails.length === 0
                                ? "Select Contacts"
                                : `${contactEmails.length} Contact${contactEmails.length !== 1 ? "s" : ""}`}
                            </span>
                            <ChevronDown className="w-3 h-3 shrink-0" />
                          </button>
                          {contactsOpen && (
                            <div className="absolute top-full left-0 z-50 mt-1 w-56 bg-popover border border-border rounded-md shadow-md p-1 max-h-44 overflow-y-auto">
                              {availableContacts.filter(c => c.email).length === 0 ? (
                                <p className="text-xs text-muted-foreground px-2 py-1">No contacts with email</p>
                              ) : (
                                availableContacts.filter(c => c.email).map(c => (
                                  <label
                                    key={c.id}
                                    className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-xs"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={contactEmails.includes(c.email!)}
                                      onChange={ev =>
                                        setContactEmails(prev =>
                                          ev.target.checked
                                            ? [...prev, c.email!]
                                            : prev.filter(x => x !== c.email)
                                        )
                                      }
                                      className="rounded shrink-0"
                                    />
                                    <span className="flex-1 min-w-0">{c.name}</span>
                                    {contactEmails.includes(c.email!) && (
                                      <Check className="w-3 h-3 text-green-600 shrink-0" />
                                    )}
                                  </label>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <input
                          type="text"
                          placeholder="email1@example.com, email2@example.com"
                          value={contactEmails.join(", ")}
                          onChange={e =>
                            setContactEmails(
                              e.target.value.split(",").map(s => s.trim()).filter(Boolean)
                            )
                          }
                          className="text-xs border border-border rounded px-2 py-1.5 bg-background min-w-[200px]"
                        />
                      )}
                    </>
                  )}
                </div>

                {/* ── Update status sub-field ── */}
                {actionType === "update_status" && (
                  <div className="space-y-1">
                    <select
                      value={workflowStatusId}
                      onChange={e => setWorkflowStatusId(e.target.value)}
                      className="text-xs border border-border rounded px-2 py-1.5 bg-background w-full"
                    >
                      <option value="">— select status —</option>
                      {(pipelineId
                        ? availablePipelines.filter(p => p.id === pipelineId)
                        : availablePipelines
                      ).map(pipeline => (
                        <optgroup key={pipeline.id} label={pipeline.name}>
                          {pipeline.stages.map(stage => (
                            <option key={stage.id} value={stage.id}>{stage.name}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* ── Create project sub-field ── */}
                {actionType === "create_project" && (
                  <div className="border border-border rounded-md bg-background overflow-hidden">
                    <div className="px-2 pt-2 pb-1 border-b border-border">
                      <input
                        placeholder="Search templates…"
                        value={templateSearch}
                        onChange={e => setTemplateSearch(e.target.value)}
                        className="w-full text-xs bg-transparent outline-none"
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {availableProjectTemplates
                        .filter(t => !templateSearch || (t.title ?? "").toLowerCase().includes(templateSearch.toLowerCase()))
                        .map(t => (
                          <button
                            key={t.id}
                            type="button"
                            onClick={() => setProjectTemplateId(t.id)}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors ${
                              projectTemplateId === t.id ? "bg-muted font-semibold text-foreground" : "text-foreground"
                            }`}
                          >
                            {t.title}
                          </button>
                        ))}
                    </div>
                    {projectTemplateId && (
                      <div className="px-3 py-1.5 border-t border-border text-xs text-violet-700 font-medium bg-violet-50">
                        ✓ {availableProjectTemplates.find(t => t.id === projectTemplateId)?.title}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Cancel / Save buttons ── */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted transition-colors"
                  >
                    Clear
                  </button>
                  {actionType && (
                    <button
                      type="button"
                      disabled={saveDisabled}
                      onClick={handleSave}
                      className="text-xs px-4 py-1.5 rounded bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 font-medium transition-colors"
                    >
                      {ruleAdding ? "Saving…" : "Save Rule"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-4 py-1.5 rounded border border-border hover:bg-muted transition-colors"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
